import type { Scenario, ScenarioContent, TavusPalBlock } from "@/lib/powtoon-be/types";
import type { ScenarioAdapter, ScenarioView, ScenarioFormState } from "../types";
import { getCachedTavusFaceThumbnail } from "./faceCache";
import { getMode } from "@/lib/scenarios/interactionModes";
import { composeBlockPrompt } from "../composePrompt";
import { strippedForeignFields } from "../spec";

export const tavusAdapter: ScenarioAdapter = {
  toView(scenario: Scenario): ScenarioView {
    const c = scenario.content ?? {};
    const pal = c.pal;
    return {
      provider: "tavus",
      name: scenario.name,
      description: scenario.description,
      thumbnailUrl:
        scenario.thumb_url ||
        (pal?.face_id ? getCachedTavusFaceThumbnail(pal.face_id) : undefined),
      systemPrompt: composeBlockPrompt(pal),
      openingText: pal?.opening_text,
      faceId: pal?.face_id,
      voiceId: pal?.elevenlabs_voice_id,
      language: c.language,
      inputs: pal?.inputs ?? [],
    };
  },
  deriveThumbnail(scenario) {
    if (scenario.thumb_url) return scenario.thumb_url;
    const faceId = scenario.content?.pal?.face_id;
    if (!faceId) return undefined;
    return getCachedTavusFaceThumbnail(faceId);
  },
  toFormState(scenario: Scenario): Partial<ScenarioFormState> {
    const c = scenario.content ?? {};
    const pal = c.pal;
    // Tavus scenarios mirror the LA split fields on `pal` so the editor
    // round-trips without loss. Older Tavus scenarios only stored the
    // composed `system_prompt` — fall back to that for `knowledge`.
    return {
      avatarId: pal?.face_id ?? "",
      voiceId: pal?.elevenlabs_voice_id ?? "",
      language: c.language ?? "",
      persona: pal?.persona ?? "",
      knowledge: pal?.knowledge ?? pal?.system_prompt ?? "",
      instructions: pal?.instructions ?? "",
      openingText: pal?.opening_text ?? "",
      mode: pal?.interaction_mode ?? "conversational",
      disableCameraSharing: pal?.disable_camera_sharing ?? false,
      disableScreenSharing: pal?.disable_screen_sharing ?? false,
      // The attached knowledge document is identified by the source it came
      // from, so the picker can re-select it even after ids change upstream.
      knowledgeDocumentSourceKey: pal?.knowledge_document_id
        ? pal.knowledge_documents?.find(
            (d) => d.document_id === pal.knowledge_document_id,
          )?.source_key
        : undefined,
      presentationEnabled: pal?.presentation?.enabled ?? false,
      internetSearchEnabled: pal?.internet_search?.enabled ?? false,
      knowledgeDocuments: pal?.knowledge_documents,
    };
  },
  collectSaveMeta(scenario) {
    const pal = scenario?.content?.pal;
    return {
      initialPalId: pal?.pal_id,
      initialElevenLabsVoiceId: pal?.elevenlabs_voice_id,
      initialQuestionnaireIds: pal?.questionnaire_ids ?? [],
      initialRoleplayIds: pal?.roleplay_ids ?? [],
    };
  },
  toContent(state: ScenarioFormState): ScenarioContent {
    const prev = state.previous ?? {};
    const prevPal = prev.pal;
    const modeMeta = getMode(state.mode);
    const knowledgeDocuments =
      state.knowledgeDocuments ?? prevPal?.knowledge_documents;
    // The selected source is the source of truth for the attached document —
    // never carry a stale id forward.
    const knowledgeDocumentId = state.knowledgeDocumentSourceKey
      ? knowledgeDocuments?.find(
          (d) => d.source_key === state.knowledgeDocumentSourceKey,
        )?.document_id
      : undefined;
    const nextPal: TavusPalBlock = {
      // Preserve the pal id so save can PATCH the existing pal.
      pal_id: prevPal?.pal_id,
      face_id: state.avatarId,
      // "" means "use face default" — persist as undefined so the Tavus
      // edge function omits the voice override.
      elevenlabs_voice_id: state.voiceId || undefined,
      // Tavus receives one composed prompt; we keep the split fields
      // alongside so the editor round-trips without loss.
      system_prompt: state.generatedPrompt,
      persona: modeMeta.showPersona ? state.persona : "",
      knowledge: state.knowledge,
      instructions: modeMeta.showInstructions ? state.instructions : "",
      opening_text: state.openingText,
      // Placeholder — saveScenario overwrites with post-upload finals.
      inputs: state.initialInputs,
      // Mode + versioning live on the pal for Tavus. `questionnaire_ids`
      // is preserved as-is here; `saveScenario.saveTavusScenario` re-writes
      // it after (optionally) creating a new questionnaire artifact.
      interaction_mode: state.mode,
      questionnaire_ids: prevPal?.questionnaire_ids,
      roleplay_ids: prevPal?.roleplay_ids,
      roleplay_scenario_id:
        state.mode === "role_play" ? prevPal?.roleplay_scenario_id : undefined,
      disable_camera_sharing: state.disableCameraSharing || undefined,
      disable_screen_sharing: state.disableScreenSharing || undefined,
      // Registration happens in the editor, so the form state is the source
      // of truth here; save only retires pairings whose source is gone.
      knowledge_documents: knowledgeDocuments,
      knowledge_document_id: knowledgeDocumentId,
      presentation:
        state.mode === "conversational" && state.knowledgeDocumentSourceKey
          ? { enabled: !!state.presentationEnabled, slides_trigger: "on_demand" }
          : undefined,
      // No document dependency — available in conversational mode only.
      internet_search:
        state.mode === "conversational"
          ? { enabled: !!state.internetSearchEnabled }
          : undefined,
    };
    return {
      ...prev,
      provider: "tavus",
      // Clears every block and top-level field owned by the other providers,
      // derived from the provider spec registry.
      ...strippedForeignFields("tavus"),
      // language is a Tavus conversation parameter — persist on content
      // so `TavusFrame` can forward it at start time.
      language: state.language || undefined,
      publish_mode: state.publishMode,
      auto_start: state.autoStart,
      cta_type: state.ctaType,
      cta_url: state.ctaType === "sales" ? state.ctaUrl.trim() : "",
      redirect_url: state.redirectUrl.trim(),
      pal: nextPal,
      display: state.display,
    };
  },
};